import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { apiClient } from '../../api/client';
import { relativeTime } from '@news-assist/shared';

interface Material {
  id: string;
  url: string;
  title: string;
  author: string | null;
  publishedAt: string | null;
  createdAt: string;
  wordCount: number;
  extractionStatus: string;
  storyId: string | null;
  sourceId: string;
}

interface MaterialsResponse {
  data: Material[];
  pagination: {
    limit: number;
    offset: number;
    count: number;
  };
}

export function MaterialsPage() {
  const { orgSlug } = useParams<{ orgSlug: string }>();
  const orgPrefix = orgSlug ? `/${orgSlug}` : '';
  const [page, setPage] = useState(0);
  const [processingId, setProcessingId] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const limit = 50;

  const handleReprocess = async (materialId: string) => {
    setProcessingId(materialId);
    try {
      await apiClient.post(`/process/material/${materialId}`);
      queryClient.invalidateQueries({ queryKey: ['materials'] });
    } catch (err) {
      alert('Failed to process: ' + (err instanceof Error ? err.message : 'Unknown error'));
    } finally {
      setProcessingId(null);
    }
  };

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['materials', page],
    queryFn: () => apiClient.get<MaterialsResponse>('/materials', {
      limit,
      offset: page * limit,
    }),
  });

  const materials = data?.data || [];
  const hasMore = data?.pagination?.count === limit;

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <div className="text-gray-500">Loading materials...</div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
        Error: {error.message}
      </div>
    );
  }

  return (
    <div>
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Imported Materials</h1>
        <p className="text-gray-600 mt-1">
          All materials clipped from news sources
        </p>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Material
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Source
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Story
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Imported
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {materials.map((material) => (
              <tr key={material.id} className="hover:bg-gray-50">
                <td className="px-4 py-3">
                  <Link
                    to={`${orgPrefix}/material/${material.id}`}
                    className="text-sm font-medium text-blue-600 hover:underline line-clamp-2"
                  >
                    {material.title}
                  </Link>
                  <div className="text-xs text-gray-400 mt-1">
                    {material.wordCount} words
                    {material.author && ` · ${material.author}`}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <span className="text-sm text-gray-700">{material.sourceId}</span>
                </td>
                <td className="px-4 py-3">
                  {material.storyId ? (
                    <Link
                      to={`${orgPrefix}/story/${material.storyId}`}
                      className="text-sm text-blue-600 hover:underline line-clamp-1"
                    >
                      View Story
                    </Link>
                  ) : (
                    <span className="text-sm text-gray-400">—</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <span
                      className={`inline-flex px-2 py-1 text-xs rounded-full ${
                        material.extractionStatus === 'completed'
                          ? 'bg-green-100 text-green-700'
                          : material.extractionStatus === 'pending'
                          ? 'bg-yellow-100 text-yellow-700'
                          : material.extractionStatus === 'failed'
                          ? 'bg-red-100 text-red-700'
                          : 'bg-gray-100 text-gray-700'
                      }`}
                    >
                      {material.extractionStatus}
                    </span>
                    {(material.extractionStatus === 'pending' || material.extractionStatus === 'failed') && (
                      <button
                        onClick={() => handleReprocess(material.id)}
                        disabled={processingId === material.id}
                        className="text-xs text-blue-600 hover:text-blue-800 disabled:text-gray-400"
                      >
                        {processingId === material.id ? 'Processing...' : 'Process'}
                      </button>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3 text-sm text-gray-500">
                  {relativeTime(material.createdAt)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {materials.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            No materials imported yet. Use the browser extension to clip materials.
          </div>
        )}

        {/* Pagination */}
        <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
          <div className="text-sm text-gray-500">
            Showing {page * limit + 1} - {page * limit + materials.length}
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              className="px-3 py-1 text-sm bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <button
              onClick={() => setPage((p) => p + 1)}
              disabled={!hasMore}
              className="px-3 py-1 text-sm bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
